import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { MeetingsMeetingGetTranscriptDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import {
  type Transcript,
  mergeConsecutiveUtterances,
} from 'modules/meetings/lib/utils/transcriptUtils'

import { MeetingTranscriptContext, type MeetingTranscriptContextState } from './index'

interface MeetingTranscriptProviderProps {
  children: ReactNode
}

export const MeetingTranscriptProvider = ({ children }: MeetingTranscriptProviderProps) => {
  const { id: meetingId } = useParams<{ id: string }>()
  const [transcript, setTranscript] = useState<Transcript | null>(null)

  // Query to get transcript data
  const {
    data: transcriptData,
    loading: loadingTranscript,
    networkActivity: networkActivityTranscript,
    refetch: refetchTranscriptQuery,
  } = useQuery(MeetingsMeetingGetTranscriptDocument, {
    variables: {
      meetingId: Number(meetingId ?? 0),
    },
    skip: !meetingId,
  })

  // Process transcript data
  useEffect(() => {
    if (transcriptData) {
      const rawTranscript = transcriptData?.meetings.transcriptsConnection.nodes[0]
      const processedTranscript =
        rawTranscript?.utterances
          ?.map((utterance, index) => {
            return {
              utterance: utterance,
              attendeeId: String(rawTranscript?.utterancesAttendeeIds[index] || ''),
              timestamp: String(rawTranscript?.utterancesTimestamps[index] || ''),
            }
          })
          .filter((utterance) => utterance.utterance !== '') || []

      // Merge consecutive utterances and set transcript
      const mergedTranscript = mergeConsecutiveUtterances(processedTranscript)
      setTranscript(mergedTranscript)
    }
  }, [transcriptData])

  const refetchTranscript = useCallback(async () => {
    await refetchTranscriptQuery()
  }, [refetchTranscriptQuery])

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue: MeetingTranscriptContextState = useMemo(
    () => ({
      transcript,
      loadingTranscript: loadingTranscript || networkActivityTranscript,
      refetchTranscript,
    }),
    [transcript, loadingTranscript, networkActivityTranscript, refetchTranscript]
  )

  return (
    <MeetingTranscriptContext.Provider value={contextValue}>
      {children}
    </MeetingTranscriptContext.Provider>
  )
}
